import {
  DEFAULT_LIST_INITIAL_STATE,
  DEFAULT_LIST_STORAGE,
  LIST_TABLE_APPEARANCE,
} from '@app/common/listTableAppearance';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { useConsumeSearchParam } from '@hooks/useConsumeSearchParam';
import { APP_PREFIX, INVENTORY_CATEGORY_PAGE } from '@app/routes/urls';
import { AppDataTable } from '@app/components/common/AppDataTable';
import { App, Button, Drawer } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createItem, deleteItem, getItem, listCategories, listItems, updateItem } from '../../api';
import type { CategoryOption } from '../InventoryForm/form.config';
import { InventoryForm } from '../InventoryForm/InventoryForm';
import InventoryMovementsSubTable from '../InventoryMovementsSubTable/InventoryMovementsSubTable';
import {
  getInventoryItemsTableColumns,
  type InventoryTableHandlers,
} from '../InventoryTable/inventoryTableColumns.config';
import RecordMovementModal from '../../RecordMovementModal';
import type { InventoryItem, InventoryItemRow } from '../../types';

/** Reduce the full form payload to the writable subset shared by create & update. */
const toWritableItem = (data: InventoryItem) => ({
  name: data.name,
  code: data.code ?? null,
  unit: data.unit,
  categoryId: data.categoryId ?? null,
  description: data.description ?? null,
  isConsumable: Boolean(data.isConsumable),
  isSellable: Boolean(data.isSellable),
  isRentable: Boolean(data.isRentable),
  reorderThreshold: data.reorderThreshold ?? null,
  defaultCost: data.defaultCost ?? null,
  defaultSalePrice: data.defaultSalePrice ?? null,
  defaultRentalRate: data.defaultRentalRate ?? null,
  defaultRentalRateUnit: data.defaultRentalRateUnit ?? null,
});

const ItemsTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const { handleError } = useSmartErrorHandling();

  const [reloadKey, setReloadKey] = useState(0);
  const [movementItem, setMovementItem] = useState<InventoryItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);

  useConsumeSearchParam('item', (value) => {
    // The API interceptor already reports a failed request.
    getItem(Number(value))
      .then((full) => {
        setEditingItem(full);
        setFormOpen(true);
      })
      .catch(() => undefined);
  });

  // Category name lookup + form options. Categories are maintained on their own
  // settings page, so leaving and returning here remounts this tab and refetches.
  const [categoryById, setCategoryById] = useState<Record<number, string>>({});
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  // Bumped once the category lookup is loaded so the items table remounts and
  // resolves category names (the initial listItems fetch runs before this
  // async lookup resolves; GenericDataTable only transforms rows at fetch time).
  const [categoriesReady, setCategoriesReady] = useState(0);

  // Keep the full DTOs from the last fetch so the record-movement modal (and the
  // edit form) receive a complete `InventoryItem`, not just the display row.
  const itemsByIdRef = useRef<Map<number, InventoryItem>>(new Map());

  useEffect(() => {
    let cancelled = false;
    listCategories({ limit: 200, offset: 0 })
      .then((response) => {
        if (cancelled) return;
        const map: Record<number, string> = {};
        const options: CategoryOption[] = [];
        response.data.forEach((category) => {
          map[category.categoryId] = category.name;
          options.push({ label: category.name, value: category.categoryId });
        });
        setCategoryById(map);
        setCategoryOptions(options);
        setCategoriesReady((c) => c + 1);
      })
      .catch(() => {
        if (!cancelled) {
          setCategoryById({});
          setCategoryOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchItems = useCallback(
    async (params: { limit: number; offset: number; query?: string; sort?: string; [key: string]: unknown }) => {
      const { limit, offset, query, sort } = params;
      const response = await listItems({ offset, limit, ...(query ? { query } : {}), ...(sort ? { sort } : {}) });
      const map = new Map<number, InventoryItem>();
      response.data.forEach((item) => map.set(item.itemId, item));
      itemsByIdRef.current = map;
      return {
        data: response.data,
        total: response.total || response.data.length,
      };
    },
    []
  );

  const handlers = useMemo<InventoryTableHandlers>(
    () => ({
      onEdit: (row) => {
        const full = itemsByIdRef.current.get(row.itemId);
        if (full) {
          setEditingItem(full);
          setFormOpen(true);
        }
      },
      onRecordMovement: (row) => {
        const item = itemsByIdRef.current.get(row.itemId);
        if (item) setMovementItem(item);
      },
      onDelete: (row) => {
        modal.confirm({
          title: t('inventory-item-delete-confirm-title'),
          content: t('inventory-item-delete-confirm-content', { name: row.name }),
          okText: t('common-delete'),
          okButtonProps: { danger: true },
          cancelText: t('common-cancel'),
          onOk: async () => {
            try {
              await deleteItem(row.itemId);
              message.success(t('inventory-item-delete-success'));
              setReloadKey((k) => k + 1);
            } catch (e) {
              handleError(e as Error, { customMessage: t('inventory-item-delete-failed') });
            }
          },
        });
      },
    }),
    [modal, message, handleError, t]
  );

  const tableConfig = useMemo(
    () => ({
      tableId: 'inventory-items',
      initialState: DEFAULT_LIST_INITIAL_STATE,
      storage: DEFAULT_LIST_STORAGE,
      urlParamConfig: { external: { searchTerm: 'search' } },
      fetchData: fetchItems,
      transformData: (item: InventoryItem): InventoryItemRow => ({
        id: item.itemId,
        itemId: item.itemId,
        name: item.name,
        categoryId: item.categoryId,
        categoryName: item.categoryId != null ? (categoryById[item.categoryId] ?? null) : null,
        unit: item.unit,
        // These carry schema defaults server-side, so they are optional on the
        // wire type; fall back to the same defaults the spec declares.
        isConsumable: item.isConsumable ?? false,
        isSellable: item.isSellable ?? false,
        isRentable: item.isRentable ?? false,
        onHand: item.onHand ?? 0,
        lowStock: item.lowStock ?? false,
        createdAt: item.createdAt,
      }),
      rowKey: 'id' as const,
      columns: getInventoryItemsTableColumns(t, handlers),
      searchPlaceholder: t('inventory-search-placeholder'),
      searchConfig: {
        minSearchLength: 3,
        debounceMs: 300,
      },
      addButton: {
        text: t('inventory-add-item'),
        onClick: () => {
          setEditingItem(null);
          setFormOpen(true);
        },
      },
      expandable: {
        expandColumnWidth: 35,
        expandColumnSticky: 'left' as const,
        expandedRowRender: (row: InventoryItemRow) => <InventoryMovementsSubTable itemId={row.itemId} />,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, fetchItems, handlers, categoryById]
  );

  const handleFormSubmit = async (data: InventoryItem) => {
    try {
      const body = toWritableItem(data);
      if (editingItem) {
        await updateItem(editingItem.itemId, body);
        message.success(t('inventory-item-update-success'));
      } else {
        await createItem(body);
        message.success(t('inventory-item-create-success'));
      }
      setFormOpen(false);
      setEditingItem(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      handleError(e as Error, {
        customMessage: editingItem ? t('inventory-item-update-failed') : t('inventory-item-create-failed'),
      });
    }
  };

  return (
    <>
      <AppDataTable
        appearance={LIST_TABLE_APPEARANCE}
        key={`${reloadKey}-${categoriesReady}`}
        config={tableConfig}
        rightContent={
          // A real link with target=_blank: opens the settings page in its own
          // browser tab so the items list keeps its search/paging state, and
          // cmd/middle-click behave as users expect.
          <Button href={`/${APP_PREFIX}/${INVENTORY_CATEGORY_PAGE}`} target="_blank" rel="noopener noreferrer">
            {t('inventory-manage-categories')}
          </Button>
        }
      />

      {/* Side drawer rather than a centred modal: the form is tall and this keeps
          the items list visible behind it while adding or editing. */}
      <Drawer
        open={formOpen}
        title={editingItem ? t('inventory-item-edit-title') : t('inventory-item-create-title')}
        onClose={() => {
          setFormOpen(false);
          setEditingItem(null);
        }}
        placement="right"
        width={640}
        destroyOnHidden
      >
        {formOpen && (
          <InventoryForm
            key={editingItem?.itemId ?? 'new'}
            data={editingItem ?? undefined}
            categoryOptions={categoryOptions}
            onSubmit={handleFormSubmit}
            extraClasses=""
          />
        )}
      </Drawer>

      <RecordMovementModal
        open={movementItem !== null}
        item={movementItem}
        onClose={() => setMovementItem(null)}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
    </>
  );
};

export default ItemsTab;
